// Types and interfaces connected to the React UI components to show filter.
// If the React UI components for filtering are used we will get a common look
// and behavior.
// There is a need to define filters and filter-values in some way since the
// same information is used in two or three different components. Representing
// filters as (dropdown) pills in the filter row. Show the same filters in
// the filter modal. And sometimes show pills for applied filters.

export enum RowVisibility {
  // Fully visible with menu
  Dropdown = "Menu",

  // Only pill that triggers modal
  PillOnly = "PillOnly",

  // Hidden
  Hidden = "Hidden",
}

// What React component to use
export enum InputType {
  Select = "Select",
  MultiSelect = "MultiSelect",
  Checkbox = "Checkbox",
  Group = "Group",
  RadioButton = "RadioButton",
}

// Is the React component for filtering or sorting
export enum FilterType {
  Filter = "Filter",
  Sort = "Sort",
}

// Base information about the filter
interface BaseFilter {
  inputType: InputType;
  id: string;
  title: string;
  enabled?: boolean;
  rowVisibility?: RowVisibility;
}

export interface GroupFilter extends BaseFilter {
  inputType: InputType.Group;
  filters: InputFilter[];
}

interface InputFilterBase extends BaseFilter {
  filterType: FilterType;
  defaultValue: FilterValue;
}
// Different filters
interface SelectFilterBase extends InputFilterBase {
  options: SelectItem[];
  showSearch?: boolean;
}

export interface SelectFilter extends SelectFilterBase {
  inputType: InputType.Select;
}

export interface MultiSelectFilter extends SelectFilterBase {
  inputType: InputType.MultiSelect;
  noResultsTitle?: string;
  noResultsDescription?: string;
  isFetching?: boolean;
}
export interface CheckboxFilter extends InputFilterBase {
  inputType: InputType.Checkbox;
  dependsOnFilterId?: string | undefined;
}
export interface RadioButtonFilter extends InputFilterBase {
  inputType: InputType.RadioButton;
  valueToSet?: string;
  idToUpdate?: string;
}

export type InputFilter =
  | SelectFilter
  | MultiSelectFilter
  | CheckboxFilter
  | RadioButtonFilter;

export type Filter = GroupFilter | InputFilter;

// Select option
interface SelectBase {
  title: string;
}

export interface SelectGroup extends SelectBase {
  options: SelectOption[];
}

interface SelectOptionBase extends SelectBase {
  // This filter depends on filter specified in this property. This property is
  // set in the filter hook and copied to filter values when an option is
  // selected.
  dependsOnFilterId?: string | undefined;

  // This filter is dependent of this filter specified by this property. This is
  // used to open/expand child/sub accordions when selected in parent filter.
  // Should be set in filter hooks.
  dependentOfFilterId?: string | undefined;
}

export interface SelectOptionString extends SelectOptionBase {
  value: string;
}
export interface SelectOptionPeriod extends SelectOptionString {
  startDate: string;
  endDate: string;
}

// String array values for options makes it possible to handle select all in an
// multi select (like any other option). Similar to select all for select
// filters. It also makes it possible to enable or disable any set of values
// which could be used to select all of a type of options.
export interface SelectOptionStringArray extends SelectOptionBase {
  value: string[];
}

export type SelectOption =
  | SelectOptionString
  | SelectOptionStringArray
  | SelectOptionPeriod;
export type SelectItem = SelectBase | SelectOption | SelectGroup;

// Filter values that can be used in filters
interface BaseFilterValue {
  filterId: string;

  // Depending filter whose values also must be cleared when this filter is
  // changed or cleared. This property should be transferred from option
  // values when option is selected.
  dependsOnFilterId?: string | undefined;

  // Dependent filter used to trigger opening of dependent filters when this
  // value is selected. This property should be transferred from option
  // values when option is selected.
  dependentOfFilterId?: string | undefined;
}

interface NullFilterValue extends BaseFilterValue {
  value: null;
}

export interface StringFilterValue extends BaseFilterValue {
  value: string;
}

interface NumberFilterValue extends BaseFilterValue {
  value: number;
}

export interface StringArrayFilterValue extends BaseFilterValue {
  value: string[];
}
export interface BooleanFilterValue extends BaseFilterValue {
  value: boolean;
}
export interface PeriodFilterValue extends StringFilterValue {
  startDate: string;
  endDate: string;
}

export type FilterValue =
  | NullFilterValue
  | StringFilterValue
  | NumberFilterValue
  | StringArrayFilterValue
  | BooleanFilterValue
  | PeriodFilterValue;
